import { useLanguage, t } from "../../../i18n";
import React, { useEffect, useMemo, useState, useRef } from "react";
import { Container, Card, Button, Badge, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { HiOutlineLocationMarker, HiOutlineArrowLeft, HiOutlineUserCircle } from "react-icons/hi";
import ListingContactActions from "../../../components/listing/ListingContactActions";
import useFloatingContactActions from "../../../hooks/useFloatingContactActions";
import Sidebar from "../../../components/layout/Sidebar/Sidebar";
import Header from "../../../components/layout/Header/Header";
import MobileOffcanvasNav from "../../../components/layout/MobileOffcanvasNav/MobileOffcanvasNav";
import NafathPromptModal from "../../../components/nafath/NafathPromptModal/NafathPromptModal";
import EmptyState from "../../../components/common/EmptyState/EmptyState";
import ListingInfoCard from "../../../components/listing/ListingInfoCard/ListingInfoCard";
import SafetyCard from "../../../components/listing/SafetyCard/SafetyCard";
import ListingImageGallery from "../../../components/listing/ListingImageGallery/ListingImageGallery";
import ReportListing from "../../../components/listing/ReportListing/ReportListing";
import VerificationNoticeBanner from "../../../components/common/VerificationNoticeBanner/VerificationNoticeBanner";
import getRoomListingDetails from "../../../features/listing/services/getRoomListingDetails";
import getFavouriteRoomListings from "../../../features/shortlist/services/getFavouriteRoomListings";
import useDocumentTitle from "../../../hooks/useDocumentTitle";
import ROUTES from "../../../config/routes";
import "./ListingDetails.css";
import Footer from "../../../components/layout/Footer/Footer";
import Logo from "../../../components/common/Logo/Logo";
import { useAuth } from "../../../features/auth/context/AuthContext";
function normalizeUser(sourceUser = {}) {
  return {
    id: sourceUser?.user_id || sourceUser?.id || "",
    user_id: sourceUser?.user_id || sourceUser?.id || "",
    name: sourceUser?.name || sourceUser?.full_name || "BaytBuddy User",
    full_name: sourceUser?.full_name || sourceUser?.name || "BaytBuddy User",
    email: sourceUser?.email || "",
    mobile: sourceUser?.mobile || "",
    profile_status: sourceUser?.profile_status || "",
    nafath_status: sourceUser?.nafath_status || ""
  };
}
function toTitleCase(value) {
  return String(value || "").replace(/_/g, " ").replace(/\s+/g, " ").trim().toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
}
function mapGenderPreference(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "male") return "Male Only";
  if (normalized === "female") return "Female Only";
  return value || "Not specified";
}
function mapFurnished(value) {
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  const normalized = String(value || "").trim().toLowerCase();
  if (["1", "true", "yes"].includes(normalized)) return "Yes";
  if (["0", "false", "no"].includes(normalized)) return "No";
  return value ? String(value) : "No";
}
function mapKitchenAccess(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "full") return "Full Access";
  if (normalized === "shared") return "Shared Access";
  if (normalized === "none") return "No Access";
  return value ? toTitleCase(value) : "Not specified";
}
function mapAcType(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "split") return "Split AC";
  if (normalized === "window") return "Window AC";
  if (normalized === "central") return "Central AC";
  return value ? toTitleCase(value) : "Not specified";
}
function mapRentPaymentPlan(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "monthly") return "Monthly";
  if (normalized === "3_months") return "3 Months";
  if (normalized === "6_months") return "6 Months";
  if (normalized === "12_months") return "12 Months";
  return value ? toTitleCase(value) : "Not specified";
}
function getRentPaymentPlanMonths(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "monthly") return 1;
  if (normalized === "3_months") return 3;
  if (normalized === "6_months") return 6;
  if (normalized === "12_months") return 12;
  return 0;
}
function mapBooleanFacility(value) {
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  const normalized = String(value || "").trim().toLowerCase();
  if (["1", "true", "yes"].includes(normalized)) return "Yes";
  if (["0", "false", "no"].includes(normalized)) return "No";
  return "No";
}
function formatPostedDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    const normalized = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
      const [year, month, day] = normalized.split("-");
      return `${day}-${month}-${year}`;
    }
    return "";
  }
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}
function formatAvailableDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}
function mapAvailability(value, availableFrom) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "immediate") {
    return "Immediate";
  }
  if (normalized === "date") {
    const formattedDate = formatAvailableDate(availableFrom);
    return formattedDate ? `Available From ${formattedDate}` : "Available From";
  }
  return "Not specified";
}
function buildAbsoluteImageUrl(imagePath) {
  const normalizedPath = String(imagePath || "").trim();
  if (!normalizedPath) {
    return "";
  }
  if (normalizedPath.startsWith("http://") || normalizedPath.startsWith("https://")) {
    return normalizedPath;
  }
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || "";
  const apiOrigin = apiBaseUrl.replace(/\/api\/v1\/?$/, "").replace(/\/$/, "");
  const cleanPath = normalizedPath.startsWith("/") ? normalizedPath : `/${normalizedPath}`;
  return `${apiOrigin}${cleanPath}`;
}
function normalizeSaudiContactNumber(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) {
    return "";
  }
  if (digits.startsWith("966")) {
    return digits;
  }
  if (digits.startsWith("0")) {
    return `966${digits.slice(1)}`;
  }
  return digits;
}
function getListerNationality(listing) {
  return listing?.user?.profile?.nationality?.name || listing?.user?.nationality?.name || listing?.user?.profile?.nationality_name || listing?.user?.nationality_name || listing?.user_nationality || listing?.nationality?.name || listing?.nationality_name || "";
}
function extractFavouriteIds(response) {
  const favouritesArray = Array.isArray(response?.data?.data?.data) ? response.data.data.data : Array.isArray(response?.data?.data) ? response.data.data : Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
  return new Set(favouritesArray.map(item => {
    return item?.room_listing?.id || item?.id || item?.room_listing_id || item?.listing_id;
  }).filter(Boolean).map(value => String(value)));
}
function mapApiListingToDetails(listing, favouriteIds = new Set()) {
  const images = Array.isArray(listing?.images) ? listing.images.map((item, index) => ({
    id: item?.id || index + 1,
    name: `Room Image ${index + 1}`,
    url: buildAbsoluteImageUrl(item?.url || item?.image_path || ""),
    isPrimary: Boolean(item?.is_primary)
  })).filter(item => item.url) : [];
  const communityList = Array.isArray(listing?.community_preferences_data) ? listing.community_preferences_data.map(item => item?.name).filter(Boolean) : [];
  const contactNumber = listing?.contact_number || "";
  const whatsappNumber = listing?.whatsapp_number || listing?.contact_number || "";
  const isFavouriteFromApi = Boolean(listing?.is_favourite);
  const isFavouriteFromFavouriteApi = favouriteIds.has(String(listing?.id));
  const isShortlisted = isFavouriteFromApi || isFavouriteFromFavouriteApi;
  return {
    id: listing?.id,
    title: listing?.title || "Untitled Listing",
    isShortlisted,
    is_favourite: isShortlisted,
    description: listing?.description || "",
    address: listing?.address || "",
    price: Number(listing?.rent || 0),
    type: toTitleCase(listing?.room_type),
    houseType: toTitleCase(listing?.house_type),
    furnished: mapFurnished(listing?.furnished),
    gender: mapGenderPreference(listing?.gender_preference),
    availability: mapAvailability(listing?.availability_type, listing?.available_from),
    city: listing?.city?.name || "",
    district: listing?.neighbourhood?.name || "",
    nationality: communityList,
    images,
    allowWhatsAppContact: Boolean(listing?.allow_whatsapp),
    allowDirectCalls: Boolean(listing?.allow_calls),
    contactNumber,
    whatsappNumber,
    userName: listing?.user_name || "BaytBuddy Lister",
    userNationality: getListerNationality(listing),
    isOwner: Boolean(listing?.is_owner),
    bedrooms: listing?.bedrooms !== undefined && listing?.bedrooms !== null ? Number(listing.bedrooms) : null,
    bathrooms: listing?.bathrooms !== undefined && listing?.bathrooms !== null ? Number(listing.bathrooms) : null,
    currentOccupants: listing?.current_occupants !== undefined && listing?.current_occupants !== null ? Number(listing.current_occupants) : listing?.max_occupants !== undefined && listing?.max_occupants !== null ? Number(listing.max_occupants) : null,
    kitchenAccess: mapKitchenAccess(listing?.kitchen_access),
    acType: mapAcType(listing?.ac_type),
    electricityIncluded: mapBooleanFacility(listing?.electricity_included),
    waterBillIncluded: mapBooleanFacility(listing?.water_bill_included),
    parkingAvailable: mapBooleanFacility(listing?.parking_available),
    rentPaymentPlan: mapRentPaymentPlan(listing?.rent_payment_plan),
    minimumStay: listing?.minimum_stay !== undefined && listing?.minimum_stay !== null ? Number(listing.minimum_stay) : null,
    postedDate: formatPostedDate(listing?.posted_date || listing?.posted_at),
    postedAgo: String(listing?.posted_ago || "").trim(),
    raw: {
      ...(listing || {}),
      is_favourite: isShortlisted
    }
  };
}
export default function ListingDetails() {
  useLanguage();
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [showNafathModal, setShowNafathModal] = useState(false);
  const [listing, setListing] = useState(null);
  const [isListingLoading, setIsListingLoading] = useState(true);
  const [listingError, setListingError] = useState("");
  const postedByRef = useRef(null);
  const showFloatingContacts = useFloatingContactActions(postedByRef, Boolean(listing) && !isListingLoading);
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const {
    user: authUser,
    isAuthenticated
  } = useAuth();
  const user = useMemo(() => normalizeUser(authUser || {}), [authUser]);
  const isProtectedShell = Boolean(isAuthenticated);
  const isProfileComplete = user?.profile_status === "complete";
  const isNafathVerified = user?.nafath_status === "verified";
  const communityList = useMemo(() => {
    return Array.isArray(listing?.nationality) ? listing.nationality : [];
  }, [listing]);
  useDocumentTitle(listing?.title || "Listing Details");
  useEffect(() => {
    let isMounted = true;
    const fetchListingDetails = async () => {
      try {
        setIsListingLoading(true);
        setListingError("");
        const [detailsResponse, favouritesResponse] = await Promise.all([getRoomListingDetails(id), isAuthenticated ? getFavouriteRoomListings() : Promise.resolve(null)]);
        const responseData = detailsResponse?.data || null;
        if (!responseData) {
          throw new Error(detailsResponse?.message || "Listing not found.");
        }
        const favouriteIds = isAuthenticated ? extractFavouriteIds(favouritesResponse) : new Set();
        const mappedListing = mapApiListingToDetails(responseData, favouriteIds);
        if (isMounted) {
          setListing(mappedListing);
        }
      } catch (error) {
        if (isMounted) {
          setListing(null);
          setListingError(error?.response?.data?.message || error?.message || "Failed to load listing details.");
        }
      } finally {
        if (isMounted) {
          setIsListingLoading(false);
        }
      }
    };
    if (id) {
      fetchListingDetails();
    } else {
      setIsListingLoading(false);
      setListingError("Listing ID is missing.");
    }
    return () => {
      isMounted = false;
    };
  }, [id, isAuthenticated]);
  const handleGalleryShortlistChange = nextIsShortlisted => {
    setListing(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        isShortlisted: nextIsShortlisted,
        is_favourite: nextIsShortlisted,
        raw: {
          ...(prev.raw || {}),
          is_favourite: nextIsShortlisted
        }
      };
    });
  };
  const handleContactOwner = () => {
    if (!isAuthenticated) {
      navigate(ROUTES.LOGIN);
      return;
    }
    if (!isProfileComplete) {
      navigate(ROUTES.COMPLETE_PROFILE);
      return;
    }
    if (!isNafathVerified) {
      setShowNafathModal(true);
      return;
    }
  };
  const handleCall = () => {
    if (!listing?.contactNumber) return;
    window.location.href = `tel:${listing.contactNumber}`;
  };
  const handleWhatsApp = () => {
    if (!listing?.whatsappNumber) return;
    const normalizedNumber = normalizeSaudiContactNumber(listing.whatsappNumber);
    if (!normalizedNumber) return;
    const listingUrl = typeof window !== "undefined" ? window.location.href : "";
    const listingTitle = String(listing?.title || "").trim();
    const whatsappMessage = `${t("Hi! I saw your listing on BaytBuddy and would like to know more.")}\n\n${listingTitle ? `${listingTitle}\n` : ""}${listingUrl}`;
    const encodedMessage = encodeURIComponent(whatsappMessage);
    window.open(`https://wa.me/${normalizedNumber}?text=${encodedMessage}`, "_blank");
  };
  const hasAnyContactMethodEnabled = Boolean(listing?.allowDirectCalls) || Boolean(listing?.allowWhatsAppContact);
  const canShowCallButton = Boolean(listing?.allowDirectCalls) && Boolean(listing?.contactNumber);
  const canShowWhatsAppButton = Boolean(listing?.allowWhatsAppContact) && Boolean(listing?.whatsappNumber);
  const requiresContactAccess = !isProfileComplete || !isNafathVerified;
  const hasContactActions = !listing?.isOwner && (requiresContactAccess || canShowCallButton || canShowWhatsAppButton);
  const contactActions = <ListingContactActions requiresAccess={requiresContactAccess} canCall={canShowCallButton} canWhatsApp={canShowWhatsAppButton} onContact={handleContactOwner} onCall={handleCall} onWhatsApp={handleWhatsApp} />;
  const rentPaymentCycleMonths = getRentPaymentPlanMonths(listing?.raw?.rent_payment_plan);
  const estimatedTotalCycleAmount = rentPaymentCycleMonths > 0 && Number(listing?.price || 0) > 0 ? Number(listing.price) * rentPaymentCycleMonths : 0;
  const pageContent = isListingLoading ? <main className="bb-listing-details-main">
    <Container fluid={isProtectedShell}>
      <div className="bb-listings-feed__loader bb-listings-feed__loader--initial">
        <Spinner animation="border" size="sm" />
        <span>{t("Loading listing details...")}</span>
      </div>
    </Container>
  </main> : listing ? <main className="bb-listing-details-main">
    <Container fluid={isProtectedShell}>
      <div className="bb-listing-details__back-wrap">
        <Button variant="link" className="bb-listing-details__back-btn" onClick={() => navigate(ROUTES.LISTINGS)}>

          <HiOutlineArrowLeft className="me-2" />{t("Back to Listings")}

        </Button>
      </div>

      <div className="bb-listing-details__layout">
        <div className="bb-listing-details__primary">
          <Card className="bb-listing-details__hero-card">
            <div className="bb-listing-details__gallery-wrap">
              <ListingImageGallery images={listing.images} title={listing.title} listingId={listing.id} isShortlisted={Boolean(listing.isShortlisted || listing.is_favourite || listing.raw?.is_favourite)} isOwner={listing.isOwner} onShortlistChange={handleGalleryShortlistChange} />

            </div>

            <Card.Body>
              <div className="bb-listing-details__badges">
                <Badge bg="primary" className="bb-primary-badge">
                  {t(listing.type)}
                </Badge>
                <Badge bg="light" className="bb-secondary-badge" text="dark">
                  {t(listing.gender)}
                </Badge>
                <Badge bg="secondary">{t(listing.houseType)}</Badge>
              </div>

              <h1 className="bb-listing-details__title">{listing.title}</h1>



              <div className="bb-listing-details__meta">
                <div className="bb-listing-details__meta-location">
                  <HiOutlineLocationMarker className="me-2" />
                  <span>
                    {t(listing.district)}, {t(listing.city)}
                  </span>
                </div>

                <div className="bb-listing-details__meta-highlight-row">
                  <div className="bb-listing-details__price-card">
                    <div className="bb-listing-details__price-label">{t("Monthly Rent")}</div>
                    <div className="bb-listing-details__price-value">{t("SAR ")}
                      {t(listing.price)}
                    </div>
                    <div className="bb-listing-details__price-subtext">{t("per month")}</div>
                  </div>

                  {listing.postedDate || listing.postedAgo ? <div className="bb-listing-details__posted-card">
                    <div className="bb-listing-details__posted-label">{t("Posted")}</div>

                    {listing.postedDate ? <div className="bb-listing-details__posted-date">
                      {t(listing.postedDate)}
                    </div> : null}

                    {listing.postedAgo ? <div className="bb-listing-details__posted-ago">
                      {t(listing.postedAgo)}
                    </div> : null}
                  </div> : null}

                  {rentPaymentCycleMonths > 0 && estimatedTotalCycleAmount > 0 ? <div className="bb-listing-details__total-card">
                    <div className="bb-listing-details__total-label">{t("Estimated Total")}

                    </div>

                    <div className="bb-listing-details__total-value">{t("SAR ")}
                      {t(estimatedTotalCycleAmount.toLocaleString())}
                    </div>

                    <div className="bb-listing-details__total-subtext">{t("for this")}
                      {t(" ")}
                      {t(rentPaymentCycleMonths === 1 ? "monthly" : `${rentPaymentCycleMonths}-month`)}{t(" ")}{t("cycle")}

                    </div>

                    {/* <div className="bb-listing-details__total-note">
                         Excludes deposit, bills, and extra charges
                         </div> */}
                  </div> : null}
                </div>
              </div>

              {communityList.length ? <div className="bb-listing-details__community-section">
                <span className="bb-listing-details__community-label">{t("Preferred Communities")}

                </span>
                <div className="bb-listing-details__community-list">
                  {communityList.map(community => <span key={community} className="bb-listing-details__community-pill">

                    {t(community)}
                  </span>)}
                </div>
              </div> : null}

              <p className="bb-listing-details__description">
                {listing.description}
              </p>
            </Card.Body>
          </Card>

          <ListingInfoCard listing={listing} />
        </div>

        <aside className="bb-listing-details__owner-column">
          <div className="bb-listing-details__sticky-sidebar">
            <Card ref={postedByRef} className="bb-listing-details__side-card">
              <Card.Body>
                <h3 className="bb-listing-details__side-title">{t("Posted By")}</h3>

                <div className="bb-listing-details__poster">
                  <div className="bb-listing-details__poster-icon">
                    <HiOutlineUserCircle />
                  </div>

                  <div>
                    <h5 className="bb-listing-details__poster-name">
                      {listing.userName}
                    </h5>

                    <p className="bb-listing-details__poster-role">{t("Listing owner")}

                    </p>

                    {listing.userNationality ? <p className="bb-listing-details__poster-nationality">
                      {t(listing.userNationality)}
                    </p> : null}
                  </div>
                </div>

                <div className="bb-listing-details__contact-box">
                  {listing.isOwner ? <>
                    <p className="bb-listing-details__contact-text">{t("This is your own post. You can manage it from your listings.")}


                    </p>
                    <Button className="w-100 bb-listing-details__contact-btn" onClick={() => navigate(ROUTES.MY_LISTINGS)}>{t("Manage Post")}


                    </Button>
                  </> : !isProfileComplete || !isNafathVerified ? <>
                    <p className="bb-listing-details__contact-text">{t("Contact details are available based on your Login and Profile Completion status.")}


                    </p>
                    {contactActions}
                  </> : hasAnyContactMethodEnabled ? <>
                    <p className="bb-listing-details__contact-text">{t("Choose how you want to contact the lister.")}

                    </p>

                    {contactActions}
                  </> : <p className="bb-listing-details__contact-text">{t("The owner has not enabled direct contact options for this listing.")}

                  </p>}

                </div>
              </Card.Body>
            </Card>

          </div>
        </aside>
        <div className="bb-listing-details__safety">
          <SafetyCard />
          <ReportListing listingId={listing.id} isOwner={listing.isOwner} />
        </div>
      </div>

      {showFloatingContacts && hasContactActions && <div className="bb-listing-details__floating-contact" data-testid="floating-contact-actions">
        {contactActions}
      </div>}

      <NafathPromptModal show={showNafathModal} onHide={() => setShowNafathModal(false)} />

    </Container>
  </main> : <main className="bb-listing-details-main">
    <Container fluid={isProtectedShell}>
      <EmptyState title={t("Listing not found")} description={t(listingError || "The listing you are trying to view does not exist or may have been removed.")} actionLabel={t("Back to Listings")} onActionClick={() => navigate(ROUTES.LISTINGS)} />

    </Container>
  </main>;
  if (!isProtectedShell) {
    return <div className="bb-public-listings-page">
      <header className="bb-public-listings-topbar">
        <Container>
          <div className="bb-public-listings-topbar__inner">
            <div className="bb-public-listings-topbar__logo" onClick={() => navigate(ROUTES.ROOT)} role="button" tabIndex={0}>

              <Logo />
            </div>

            <div className="bb-public-listings-topbar__actions">
              <Button variant="outline-primary" className="bb-brand-btn-outline" onClick={() => navigate(ROUTES.ROOT)}>{t("Back to Home")}


              </Button>
              <Button className="bb-login-register-btn" onClick={() => navigate(ROUTES.LOGIN)}>{t("Login")}


              </Button>
            </div>
          </div>
        </Container>
      </header>

      {t(pageContent)}

      <Footer />
    </div>;
  }
  return <div className="bb-dashboard-layout">
    <div className="bb-dashboard-layout__sidebar d-none d-lg-block">
      <Sidebar user={user} />
    </div>

    <div className="bb-dashboard-layout__content">
      <Header onMenuClick={() => setShowMobileNav(true)} />

      <VerificationNoticeBanner user={user} />

      <MobileOffcanvasNav show={showMobileNav} onHide={() => setShowMobileNav(false)} user={user} />


      {t(pageContent)}
      <Footer />
    </div>
  </div>;
}
